import { configuration } from "./Configuration";
import {
  ApplicationError,
  FileTooLargeError,
  TenderPlanLimitError,
  UnauthorizedError,
} from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";
import { useAtom } from "jotai";
import { authenticationAtom } from "./State";

export interface TenderSearchProfileInput {
  name: string;
  keywords?: string[];
  keywordExpressions?: string[];
  cpvCodes?: string[];
  country?: string;
  regions?: string[] | null;
  roles?: string[];
  /** Ausschlusswörter — der Server prüft sie nur gegen den Titel einer Ausschreibung. */
  excludedTitleTerms?: string[];
}

export interface TenderSearchProfile extends TenderSearchProfileInput {
  _id: string;
  customer: string;
}

export interface TenderProfileSuggestion {
  keywords: string[];
  keywordExpressions: string[];
  cpvCodes: string[];
  /** Fehlt bei einem älteren Server. */
  excludedTitleTerms?: string[];
}

const processResponse = async (response: Response) => {
  if (response.ok) {
    return response.json();
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  // Creating a profile beyond the tender plan's allowance: a refusal with numbers, not a failure.
  const planLimit = await TenderPlanLimitError.fromResponse(response);
  if (planLimit) {
    throw planLimit;
  }

  let errorMessage = `${response.status} ${response.statusText}`;

  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // response body not JSON
  }

  throw new ApplicationError(errorMessage);
};

/**
 * One place of performance ("Ausführungsort") as the detailed tender analysis found it.
 * `label` is rendered server-side, so the portal never re-assembles the address itself.
 */
export interface TenderPerformanceLocation {
  label: string;
  name?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  region?: string;
  country?: string;
  /** Los-Bezeichnung, when the analysis assigned the place to a lot. */
  lot?: string;
  note?: string;
}

export interface Tender {
  _id: string;
  publicationNumber: string;
  title: Record<string, string>;
  titleLot?: Record<string, string[]>;
  descriptionLot?: Record<string, string[]>;
  descriptionProc?: Record<string, string[]>;
  additionalInformationLot?: Record<string, string[]>;
  additionalInfoProc?: Record<string, string[]>;
  buyer?: {
    name?: Record<string, string[]>;
    street?: string | null;
    city?: string | null;
    postCode?: string | null;
    country?: string | null;
    email?: string | null;
    contactPoint?: string | null;
    internetAddress?: string | null;
    identifier?: string | null;
    legalType?: string | null;
  };
  contractNature?: string | string[] | null;
  noticeType?: string | null;
  procedureType?: string | null;
  formType?: string | null;
  lotIdentifier?: string | null;
  referenceNumber?: string | null;
  procedureIdentifier?: string | null;
  cpvCodes?: string[];
  publicationDate?: string | null;
  dispatchDate?: string | null;
  deadline?: string | null;
  contractDuration?: {
    startDate?: string | null;
    endDate?: string | null;
    period?: string | null;
  };
  country?: string | null;
  placesOfPerformance?: string[];
  performanceStreet?: string | null;
  performanceCity?: string | null;
  performancePostCode?: string | null;
  performanceCountry?: string | null;
  estimatedValue?: number | null;
  estimatedValueCurrency?: string | null;
  frameworkAgreement?: string | null;
  awardCriteria?: { type: string; name?: string | null; weight?: string | null }[];
  selectionCriteria?: Record<string, string[]>;
  legalBasis?: string | null;
  mainActivity?: string | null;
  documents?: { urls?: string[]; submissionUrls?: string[] };
  submissionLanguage?: string[];
  electronicSubmission?: string | null;
  variantAllowed?: string | null;
  tenderValidityDeadline?: any;
  suitableForSme?: boolean | null;
  officialLanguage?: string[];
  links?: Record<string, any>;
  matchedProfiles?: { profile: string; matchedAt: string }[];
  createdAt?: string;
  updatedAt?: string;
  // External original-tender page crawled by the server (only present on the
  // single-tender endpoint; projected out of the list for size).
  crawledMarkdown?: string;
  crawledUrl?: string;
  crawledAt?: string;
  crawlStatus?: "pending" | "ok" | "failed";
  crawledDocuments?: {
    url: string;
    fileName: string;
    status: "ok" | "download-failed" | "not-a-pdf" | "store-failed";
    managedFileId?: string;
    markdown?: string;
    error?: string;
  }[];
  // Team-wide (per-customer) flags, merged in by the server.
  favorite?: boolean;
  archived?: boolean;
  // Places of performance taken over from the detailed analysis — per customer, merged in by the
  // server on both the list and the single-tender endpoint. Three states, and they differ:
  //   undefined/null → no analysis has run yet
  //   []             → an analysis ran and found none
  //   [...]          → the places of the last analysis
  performanceLocations?: TenderPerformanceLocation[] | null;
  performanceLocationsUpdatedAt?: string | null;
  /** The fit light, when a process step judged the tender (tri-server docs/tender-fit-score.md). */
  fit?: TenderFit | null;
  fitFeedback?: TenderFitFeedback | null;
}

/**
 * Passung (Ampel). Exists only where a process step of the tenant judged the tender against its
 * criteria; null otherwise — never a guess. The score is shown on hover only.
 */
export interface TenderFit {
  /** 0–1. */
  score: number;
  level: "green" | "yellow" | "red";
  /** At most three, an unmet (knock-out) criterion first. */
  reasons: { effect: "positive" | "negative" | "neutral" | string; text: string }[];
}

/** „Passt“ / „Passt nicht“ — calibration data, it never changes the light. */
export interface TenderFitFeedback {
  verdict: "fits" | "notFits";
  score: number | null;
  by: string | null;
  at: string | null;
}

/** One metered or counted allowance of the tender plan. `included: null` means unlimited. */
export interface TenderPlanAllowance {
  used: number;
  included: number | null;
}

/**
 * `GET /:customer/tenders/usage` — the customer's tender plan and what this month used of it.
 * `tier: null` is a legacy customer without a plan: no limits, and nothing to show.
 */
export interface TenderPlanUsage {
  tier: "radar" | "bid" | "group" | null;
  period: { from: string; to: string };
  analyses: TenderPlanAllowance;
  concepts: TenderPlanAllowance;
  profiles: TenderPlanAllowance;
  users: TenderPlanAllowance;
  autoStart: { enabled: boolean; monthlyCap: number | null; startedThisPeriod: number };
}

export interface TenderStateInput {
  favorite?: boolean;
  archived?: boolean;
}

// --- Ausschreibungs-Board ------------------------------------------------
// One row per tender, carrying the BPM process running on it. The server does the join across two
// databases and resolves the German phase labels, so nothing here duplicates that vocabulary.

export interface BoardAttention {
  level: "critical" | "warning" | "normal";
  deadlineDays: number | null;
  overdue: boolean;
  dueSoon: boolean;
  stalled: boolean;
  unowned: boolean;
}

/**
 * The `tender.locate` answer. Everything here is computed server-side; the map layers arrive ready
 * to draw in `__resources[0].data.layers`.
 */
export interface TenderLocationResult {
  publicationNumber: string | null;
  title?: string;
  /** Resolved place of performance incl. its precision, or null when nothing could be derived. */
  location: { lat: number; lon: number; label: string; precision?: string } | null;
  /** Set when the location is coarse (a Kreis/Bundesland centroid), so the UI can say so. */
  precisionNote?: string | null;
  /** Nur das Bundesland ist bekannt — dann nennt der Server keine Entfernungen. */
  distancesWithheld?: boolean;
  radiusKm?: number;
  /** The closest branch, named even when it lies outside the radius. */
  nearest?: any;
  locations?: any[];
  /** ALL projects in the radius, uncapped — the drawn ones are `projects`. */
  projectCount?: number;
  nearestProject?: any;
  projects?: any[];
  __resources?: { type: string; title?: string; data?: any; options?: any }[];
}

/** One dated (or deliberately undated) milestone of a tender. Everything — the German label, the
 *  urgency grade, the chronological order — is resolved server-side. */
/** A date of the analysis that contradicts the published one — a hint, never a second deadline. */
export interface TenderDateConflict {
  key: string;
  label: string;
  notice: { at: string | null; origin?: string };
  other: { label: string; at: string | null; origin?: string; source: string };
}

/** `GET /:customer/tender-board/:tenderId` — the notice fields the board row leaves out. */
/** Where the documents ask for a chapter — the model's citation (file, page when the file has pages). */
export interface TenderConceptSource {
  file: string;
  page: number | null;
}

/** The outline read from the documents, waiting for „Passt die Gliederung?“ (tri-server `conceptOutline`). */
export interface TenderConceptOutline {
  chapters: Array<{
    heading: string;
    requirement: string;
    lengthNote: string | null;
    evaluation: string | null;
    lot: string | null;
    concept: string | null;
    sources?: TenderConceptSource[];
  }>;
  /** Files not or only partly read (too large or without text). */
  unread: string[];
  documentsRead: number;
  hint: string | null;
  readAt: string;
}

export type TenderConceptChapterStatus = "fertig" | "platzhalter" | "geruest";

/** The written concept (tri-server `concept`): status per chapter, the .docx, the notes. */
export interface TenderConceptDocument {
  company: string;
  writtenAt: string;
  chapters: Array<{
    heading: string;
    requirement: string;
    sources: TenderConceptSource[];
    status: TenderConceptChapterStatus;
    openPlaceholders: number;
    /** Markdown as written into the .docx. */
    text: string;
  }>;
  openPlaceholders: number;
  /** `webUrl` only when the files live on SharePoint (opens Word Online). */
  documents: Array<{ fileId: string; name: string; url: string; webUrl?: string }>;
  notes: string[];
}

export interface TenderPanelConcept {
  outline: TenderConceptOutline | null;
  document: TenderConceptDocument | null;
}

export interface TenderPanelFacts {
  leistung: string | null;
  laufzeit: string | null;
  verfahren: string | null;
  vergabenummer: string | null;
  publicationNumber: string | null;
  plattform: { url: string; host: string } | null;
}

export interface TenderDate {
  key: string;
  label: string;
  /** ISO instant, or null when only a relative statement is known ("4 Wochen nach Arbeitsaufnahme"). */
  at: string | null;
  /** notice = published, derived = computed by us from a published period, analysis = from the documents. */
  source: "notice" | "derived" | "analysis" | "manual";
  note?: string;
  origin?: string;
  daysUntil: number | null;
  level: "past" | "critical" | "soon" | "future" | "unknown";
  /** Only on manual entries — who typed it, and when. */
  createdBy?: string | null;
  createdAt?: string;
}

export interface TenderBoardRow {
  tenderId: string;
  publicationNumber: string | null;
  title: string;
  buyer: string | null;
  place: string | null;
  source: string | null;
  deadline: string | null;
  publishedAt: string | null;
  /** Places of performance from the detailed analysis — several, and not the same thing as `place`,
   *  which is the single city the notice itself published. `null` = never analysed, `[]` = analysed
   *  and none found. */
  performanceLocations?: TenderPerformanceLocation[] | null;
  performanceLocationsUpdatedAt?: string | null;
  /** The tender's milestones, chronological. Never empty just because no analysis has run — the
   *  notice's own dates carry it. */
  dates?: TenderDate[];
  analysisDatesUpdatedAt?: string | null;
  profileIds: string[];
  multiProfile: boolean;
  phase: string | null;
  phaseLabel: string | null;
  phaseOrder: number;
  outcome: string | null;
  outcomeLabel: string | null;
  ownerUserId: string | null;
  responsibleRoleId: string | null;
  /** Display name of the responsible department. Prefer this over `currentStep.role`, which carries
   *  the BPMN lane name and goes stale after a Weitergabe. */
  responsibleRoleName: string | null;
  triageState: string | null;
  process: {
    instanceId: string;
    status: string | null;
    synopsis: string | null;
    conversationId: string | null;
    startedAt: string | null;
  } | null;
  siblingProcessCount: number;
  currentStep: {
    taskId: string;
    name: string | null;
    status: string | null;
    role: string | null;
    roleId: string | null;
    assigneeUserId: string | null;
    /** The question put to the human. */
    userPrompt: string | null;
    /** `slider` (Ja/Nein) | `confirmation` (Bestätigt) | anything else = free text. */
    uxElement: string;
    /** Button texts from the BPMN (`tri:yesLabel` …) — wording only, the answer sent stays the same.
     *  Absent from servers before the concept step (tri-server docs/tender-concept-step.md). */
    labels?: { yes: string | null; no: string | null; confirm: string | null };
    /** What the panel shows next to the question: `bid-decision`, `concept-outline`, `concept-document`. */
    context?: string | null;
  } | null;
  lastAction: {
    at: string;
    eventType: string | null;
    label: string | null;
    actor: string | null;
    actorName: string | null;
  } | null;
  attention: BoardAttention;
  /** One status instead of phase plus step — resolved server-side (Triage, Prüfung, Bid/No-Bid, …). */
  status: { key: TenderBoardStatusKey; label: string };
  /** What is open, in words; null when nothing waits on a human. */
  openTask: string | null;
  /** `count` is null while the crawler has not been there yet — not the same as 0. */
  documents: { count: number | null; url: string | null };
  /** Name of the matching customer in the own CRM, when the precomputed score reaches at least
   *  „Wahrscheinlich Bestandskunde“ (tri-server docs/tender-existing-customer-score.md). */
  existingCustomer: string | null;
  /** The precomputed match behind `existingCustomer`; absent from servers before the score. Kept
   *  after a „Kein Kunde“ (then `existingCustomer` is null), so the verdict can be undone. */
  existingCustomerMatch?: TenderBoardExistingCustomerMatch | null;
  /** What a person said about that match — „Ja, ist Bestandskunde“ / „Kein Kunde“. */
  existingCustomerFeedback?: { verdict: "confirmed" | "rejected"; by: string | null; at: string | null } | null;
  /** The fit light; absent from servers before it, null when no process step judged the tender. */
  fit?: TenderFit | null;
  fitFeedback?: TenderFitFeedback | null;
  /** Analysis dates that contradict a published one (the panel's „Abweichung“ hint). */
  dateConflicts?: TenderDateConflict[];
  firstSeenAt: string | null;
  isNew: boolean;
  /** The Angebotsfrist has passed and the bid is not in. */
  expired: boolean;
}

export type TenderBoardExistingCustomerReason =
  | { kind: "name"; similarity: number; crmName: string }
  | { kind: "buyerAddress"; address: string }
  | { kind: "objectAddress"; addresses: string[]; exact: boolean }
  | { kind: "locality"; place: string };

/** `high` → „Bestandskunde“, `medium` → „Wahrscheinlich Bestandskunde“. */
export interface TenderBoardExistingCustomerMatch {
  level: "high" | "medium";
  /** 0–1. */
  score: number;
  name: string;
  gesellschaften: string[];
  address: string | null;
  reasons: TenderBoardExistingCustomerReason[];
}

export type TenderBoardStatusKey =
  | "triage"
  | "pruefung"
  | "bid"
  | "angebot"
  | "abgegeben"
  | "ergebnis"
  | "verworfen";

export type TenderBoardQuickFilter =
  | "new"
  | "dueThisWeek"
  | "noDocuments"
  | "noOwner"
  | "existingCustomer"
  | "fitGreen"
  | "fitDisputed";

/** Counts for the focus line, the quick-filter chips and the status switch. */
export interface TenderBoardFocus {
  new: number;
  dueThisWeek: number;
  withoutDocuments: number;
  noOwner: number;
  existingCustomer: number;
  /** Open rows shown green (the person's verdict wins); absent from servers before it. */
  fitGreen?: number;
  /** Open rows where a person contradicted the light; absent from servers before it. */
  fitDisputed?: number;
  /** Left out because the deadline passed; 0 unless `hideExpired`. */
  expiredHidden: number;
  views: { triage: number; inProgress: number; all: number };
}

/** `GET /:customer/tenders/:id/existing-customer` — the fuzzy Bestandskunden-Prüfung. */
export interface TenderExistingCustomerCheck {
  tenderId: string;
  buyer: string | null;
  /** False when the customer has no CRM at all — not the same as "no match". */
  crm: boolean;
  candidates: { name: string; score: number; exact: boolean }[];
}

export interface TenderBoardSummary {
  total: number;
  withProcess: number;
  triage: number;
  overdue: number;
  dueSoon: number;
  stalled: number;
  unowned: number;
  multiProfile: number;
  focus: TenderBoardFocus;
}

export interface TenderBoardResponse {
  summary: TenderBoardSummary;
  total: number;
  offset: number;
  limit: number;
  rows: TenderBoardRow[];
}

export interface TenderActivityEntry {
  at: string;
  origin: "tender" | "process";
  kind: string;
  actor: string | null;
  actorName: string | null;
  label: string;
  detail: Record<string, unknown>;
}

/** A process conversation, as the existing `/:customer/conversations/:id` endpoint serves it. */
export interface ProcessConversation {
  _id: string;
  topic?: string;
  user?: string;
  messages?: any[];
}

/** A tender added by hand, as the server reports it once it exists. */
export interface ManualTenderCreated {
  status: "created";
  tenderId: string;
  publicationNumber: string;
  title: string;
  /** How many uploaded documents it was linked to. */
  documents: number;
  deadline: string | null;
  message: string;
}

export type ManualTenderIntake =
  | { status: "processing"; intakeId?: string }
  | ManualTenderCreated
  | { status: "failed"; error: string };

export interface TenderBoardQuery {
  view?: "all" | "process" | "triage";
  attention?: "overdue" | "dueSoon" | "stalled" | "unowned";
  roleId?: string;
  ownerUserId?: string;
  search?: string;
  quick?: TenderBoardQuickFilter;
  /** Opt-in on the server; the board sends it while "Abgelaufene anzeigen" is off. */
  hideExpired?: boolean;
  sort?: "deadline" | "newest" | "fit";
  limit?: number;
  offset?: number;
}

export const useTenderSearchProfileAPI = () => {
  const [authentication] = useAtom(authenticationAtom) as any;
  const baseUrl = `${configuration.serverUrl}/${authentication.customer}/tender-search-profiles`;
  const tendersBaseUrl = `${configuration.serverUrl}/${authentication.customer}/tenders`;

  const get = async (path = "") => {
    const response = await authenticatedFetch(`${baseUrl}${path}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return processResponse(response);
  };

  const post = async (path: string, body: any) => {
    const response = await authenticatedFetch(`${baseUrl}${path}`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
    });
    return processResponse(response);
  };

  const getTenders = async (path: string) => {
    const response = await authenticatedFetch(`${tendersBaseUrl}${path}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return processResponse(response);
  };

  const postTenders = async (path: string, body: any) => {
    const response = await authenticatedFetch(`${tendersBaseUrl}${path}`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
    });
    return processResponse(response);
  };

  const deleteTenders = async (path: string) => {
    const response = await authenticatedFetch(`${tendersBaseUrl}${path}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    });
    return processResponse(response);
  };

  // The board sits on its own path, not under /tenders — it is the joined view, not a tender read.
  const boardFetch = async (path: string) => {
    const response = await authenticatedFetch(
      `${configuration.serverUrl}/${authentication.customer}/tender-board${path}`,
      { method: "GET", headers: { "Content-Type": "application/json" } }
    );
    return processResponse(response);
  };

  return {
    list: (): Promise<TenderSearchProfile[]> => get(),
    getById: (id: string): Promise<TenderSearchProfile> => get(`/${id}`),
    create: (profile: TenderSearchProfileInput): Promise<TenderSearchProfile> =>
      post("", profile),
    update: (id: string, profile: TenderSearchProfileInput): Promise<TenderSearchProfile> =>
      post(`/${id}`, profile),
    remove: (id: string): Promise<{ message: string }> =>
      post(`/${id}/delete`, {}),
    search: (id: string): Promise<any> =>
      post(`/${id}/search`, {}),
    suggest: (description: string): Promise<TenderProfileSuggestion> =>
      post("/suggest", { description }),
    // Without profileId, returns the tenders of all the customer's profiles.
    listTenders: (profileId?: string): Promise<Tender[]> =>
      getTenders(profileId ? `?profileId=${profileId}` : ""),
    getTender: (id: string): Promise<Tender> =>
      getTenders(`/${id}`),
    /** The tender plan and this month's usage (`period` as `YYYY-MM`, default: current month). */
    getUsage: (period?: string): Promise<TenderPlanUsage> =>
      getTenders(`/usage${period ? `?period=${encodeURIComponent(period)}` : ""}`),
    setTenderState: (
      id: string,
      state: TenderStateInput
    ): Promise<{ tenderId: string; favorite: boolean; archived: boolean }> =>
      postTenders(`/${id}/state`, state),

    // --- Board ---
    getBoard: (query: TenderBoardQuery = {}): Promise<TenderBoardResponse> => {
      const params = new URLSearchParams();
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });
      const suffix = params.toString();
      return boardFetch(suffix ? `?${suffix}` : "");
    },
    /** One row for the side panel — also outside the current filter — plus its Kerndaten. */
    getTenderPanel: (id: string): Promise<{ row: TenderBoardRow; facts: TenderPanelFacts; concept?: TenderPanelConcept }> =>
      boardFetch(`/${encodeURIComponent(id)}`),
    /** „Ja, ist Bestandskunde“ / „Kein Kunde“, or null to undo. The score itself stays. */
    setExistingCustomerFeedback: (
      id: string,
      verdict: "confirmed" | "rejected" | null,
      name: string | null
    ): Promise<{ tenderId: string; changed: boolean; verdict: "confirmed" | "rejected" | null }> =>
      postTenders(`/${id}/existing-customer/feedback`, { verdict, name }),
    /** „Passt“ / „Passt nicht“ on the fit light, or null to undo. The light itself stays. */
    setTenderFitFeedback: (
      id: string,
      verdict: "fits" | "notFits" | null
    ): Promise<{ tenderId: string; changed: boolean; verdict: "fits" | "notFits" | null }> =>
      postTenders(`/${id}/fit/feedback`, { verdict }),
    getTenderActivity: (
      id: string,
      limit = 100
    ): Promise<{ tenderId: string; total: number; entries: TenderActivityEntry[] }> =>
      getTenders(`/${id}/activity?limit=${limit}`),
    /**
     * Where the tender is, with the Niederlassungen and running projects around it. The response is
     * the `tender.locate` tool's own `structuredContent`, so `__resources[0]` is a ready-made map
     * resource — the portal renders it, it does not compute geography.
     */
    getTenderLocation: (id: string, radiusKm?: number): Promise<TenderLocationResult> =>
      getTenders(`/${id}/location${radiusKm ? `?radiusKm=${radiusKm}` : ""}`),
    /** Add a date by hand. Stored apart from the analysis' own, so a re-run cannot wipe it. */
    addTenderDate: (
      id: string,
      date: { label: string; at?: string; note?: string }
    ): Promise<{ tenderId: string; date: TenderDate; manualDates: TenderDate[] }> =>
      postTenders(`/${id}/dates`, date),
    removeTenderDate: (id: string, key: string): Promise<{ tenderId: string; manualDates: TenderDate[] }> =>
      deleteTenders(`/${id}/dates/${encodeURIComponent(key)}`),
    setTenderOwner: (
      id: string,
      ownerUserId: string | null
    ): Promise<{ tenderId: string; changed: boolean; ownerUserId: string | null }> =>
      postTenders(`/${id}/owner`, { ownerUserId }),
    handOverTender: (
      id: string,
      responsibleRoleId: string,
      keepOwner = false
    ): Promise<{
      tenderId: string;
      changed: boolean;
      responsibleRoleId: string | null;
      clearedOwner: boolean;
      tasksRetargeted: number;
    }> => postTenders(`/${id}/handover`, { responsibleRoleId, keepOwner }),
    /**
     * Start a process on a tender. Takes the WORKFLOW id, not a definition id: the server deploys
     * it on the way in (idempotently), the way every other start path does. Requiring a deployed
     * definition used to make this depend on someone having switched event detection on.
     */
    startTenderProcess: (
      id: string,
      workflowId: string,
      responsibleRoleId?: string | null
    ): Promise<{ tenderId: string; instanceId: string; businessKey: string }> =>
      postTenders(`/${id}/start-process`, { workflowId, responsibleRoleId }),
    dismissTender: (
      id: string,
      reason?: string
    ): Promise<{ tenderId: string; changed: boolean; triageState: string }> =>
      postTenders(`/${id}/dismiss`, { reason }),
    restoreTender: (id: string): Promise<{ tenderId: string; triageState: string }> =>
      postTenders(`/${id}/dismiss`, { restore: true }),
    /** The buyer against the own CRM, fuzzy, with scores. Reads only. */
    checkExistingCustomer: (id: string): Promise<TenderExistingCustomerCheck> =>
      getTenders(`/${id}/existing-customer`),

    /**
     * Add a tender by hand — from tender documents, a free text, or both. Text alone is answered
     * right away (`created`); with files the server answers `processing` and finishes on its own,
     * so the dialog polls `getManualTenderIntake` only to show when it is done.
     */
    createManualTender: async (files: File[], text: string): Promise<ManualTenderIntake> => {
      const form = new FormData();
      // The name as the user sees it: the server reads the multipart filename as UTF-8.
      files.forEach((file) => form.append("files", file, file.name));
      form.append("text", text);
      const response = await authenticatedFetch(`${tendersBaseUrl}/manual`, {
        method: "POST",
        body: form,
        // No Content-Type: the browser sets it, boundary included.
      });
      if (response.status === 413) {
        const body = await response.json().catch(() => ({}) as any);
        throw new FileTooLargeError(body.maxFileSizeBytes);
      }
      return processResponse(response);
    },
    getManualTenderIntake: (intakeId: string): Promise<ManualTenderIntake> =>
      getTenders(`/manual/${encodeURIComponent(intakeId)}`),

    /**
     * The process conversation, through the portal's existing endpoint rather than a tender-specific
     * one — it already carries the access rule these conversations need: they are owned by the
     * system user, so a real user is admitted via their worklist, not via ownership. A 403 here is
     * the rule working, not a bug.
     */
    getProcessConversation: async (conversationId: string): Promise<ProcessConversation> => {
      const response = await authenticatedFetch(
        `${configuration.serverUrl}/${authentication.customer}/conversations/${encodeURIComponent(conversationId)}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );
      return processResponse(response);
    },
  };
};
